import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import { useAuth0 } from '@auth0/auth0-react';
import { apiRequest, ApiError } from '../api/client';
import { categorize, readDroppedItems, type Categorized } from './folderWalk';

const TUS_ENDPOINT = (import.meta.env.VITE_TUS_ENDPOINT as string | undefined) ?? 'https://tus.transfer.dfx.network/files/';
const PARALLELISM = 4;

interface UploadDropZoneProps {
  /** When omitted, the component asks the API to mint a new batch ID. */
  initialBatchId?: string;
  /**
   * If true, the file/folder chooser opens automatically on mount.
   * Used by the dedicated /upload page where the user just clicked
   * a "Begin upload" button — the navigation gesture is recent
   * enough that browsers allow the programmatic input click.
   */
  autoOpenChooser?: boolean;
}

type UsableCategorized = Categorized;

interface UploadItem {
  id: string;
  categorized: UsableCategorized;
  state: 'queued' | 'uploading' | 'done' | 'failed';
  bytesUploaded: number;
  bytesTotal: number;
  error?: string;
  upload?: tus.Upload;
}

export function UploadDropZone({ initialBatchId, autoOpenChooser }: UploadDropZoneProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [batchId, setBatchId] = useState<string | null>(initialBatchId ?? null);
  const [requestingBatch, setRequestingBatch] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [notifyState, setNotifyState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [lastDropDebug, setLastDropDebug] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allDone = items.length > 0 && items.every((i) => i.state === 'done');
  const anyFailed = items.some((i) => i.state === 'failed');
  const overallProgress = useMemo(() => {
    if (items.length === 0) return 0;
    const total = items.reduce((s, i) => s + i.bytesTotal, 0);
    const done = items.reduce((s, i) => s + i.bytesUploaded, 0);
    return total === 0 ? 0 : Math.floor((done / total) * 100);
  }, [items]);

  const ensureBatchId = useCallback(async (): Promise<string | null> => {
    if (batchId) return batchId;
    setRequestingBatch(true);
    setBatchError(null);
    try {
      const token = await getAccessTokenSilently();
      const resp = await apiRequest<{ batch_id: string }>('/api/v1/batches/new', {
        token,
        method: 'POST',
      });
      setBatchId(resp.batch_id);
      return resp.batch_id;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setBatchError(msg);
      return null;
    } finally {
      setRequestingBatch(false);
    }
  }, [batchId, getAccessTokenSilently]);

  async function handleFiles(categorized: Categorized[]) {
    setDropError(null);
    if (categorized.length === 0) {
      setDropError(
        "We didn't see any files in that drop. If your browser is blocking folder access, try clicking the drop zone instead and pick the folder."
      );
      return;
    }
    const usable = categorized;

    const id = await ensureBatchId();
    if (!id) {
      setDropError('Could not generate a batch ID. See error above.');
      return;
    }
    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch (err) {
      setDropError(`Auth0 token fetch failed: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    const newItems: UploadItem[] = usable.map((c) => ({
      id: `${c.relativePath}-${Math.random().toString(36).slice(2, 10)}`,
      categorized: c,
      state: 'queued',
      bytesUploaded: 0,
      bytesTotal: c.file.size,
    }));
    setItems((prev) => [...prev, ...newItems]);
    runQueue(newItems, id, token);
  }

  function runQueue(queued: UploadItem[], id: string, token: string) {
    let inFlight = 0;
    let cursor = 0;
    const startNext = () => {
      while (inFlight < PARALLELISM && cursor < queued.length) {
        const item = queued[cursor++]!;
        inFlight++;
        startUpload(item, id, token).finally(() => {
          inFlight--;
          startNext();
        });
      }
    };
    startNext();
  }

  async function startUpload(item: UploadItem, id: string, token: string) {
    return new Promise<void>((resolve) => {
      let meta: Record<string, string>;
      if (item.categorized.kind === 'metadata_csv') {
        meta = {
          kind: 'metadata_csv',
          access_token: token,
          batch_id: id,
        };
      } else if (item.categorized.kind === 'asset') {
        meta = {
          kind: 'asset',
          access_token: token,
          batch_id: id,
          provider_id: item.categorized.providerId,
          subdir: item.categorized.subdir,
          filename: item.categorized.filename,
        };
      } else {
        meta = {
          kind: 'raw_file',
          access_token: token,
          batch_id: id,
          relative_path: item.categorized.relativePath,
        };
      }
      const upload = new tus.Upload(item.categorized.file, {
        endpoint: TUS_ENDPOINT,
        chunkSize: 50 * 1024 * 1024,
        retryDelays: [0, 2000, 5000, 10000, 20000],
        metadata: meta,
        onError: (err) => {
          setItems((prev) =>
            prev.map((it) => (it.id === item.id ? { ...it, state: 'failed', error: String(err) } : it))
          );
          resolve();
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setItems((prev) =>
            prev.map((it) => (it.id === item.id ? { ...it, state: 'uploading', bytesUploaded, bytesTotal } : it))
          );
        },
        onSuccess: () => {
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id ? { ...it, state: 'done', bytesUploaded: item.bytesTotal } : it
            )
          );
          resolve();
        },
      });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, upload } : it)));
      upload.start();
    });
  }

  async function notifyDfx() {
    if (!batchId) return;
    setNotifyState('sending');
    setNotifyError(null);
    try {
      const token = await getAccessTokenSilently();
      await apiRequest(`/api/v1/batches/${encodeURIComponent(batchId)}/notify`, {
        token,
        method: 'POST',
      });
      setNotifyState('sent');
    } catch (err) {
      setNotifyState('failed');
      setNotifyError(err instanceof ApiError ? err.message : String(err));
    }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(true);
  }
  function onDragLeave() {
    setDragActive(false);
  }
  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    setDropError(null);
    try {
      // DataTransferItemList becomes invalid after the first await, so
      // pull entries synchronously before any async work.
      const droppedFiles = await readDroppedItems(e.dataTransfer.items);
      const cat = categorize(droppedFiles);
      const summary =
        `dropped ${droppedFiles.length} file(s) — ` +
        `${cat.filter((c) => c.kind === 'asset').length} asset, ` +
        `${cat.filter((c) => c.kind === 'metadata_csv').length} metadata.csv, ` +
        `${cat.filter((c) => c.kind === 'raw_file').length} raw`;
      setLastDropDebug(summary);
      // eslint-disable-next-line no-console
      console.log('[upload]', summary, cat);
      await handleFiles(cat);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDropError(`Drop handler failed: ${msg}`);
      // eslint-disable-next-line no-console
      console.error('[upload] drop failed', err);
    }
  }
  function onPickClick() {
    inputRef.current?.click();
  }
  async function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    setDropError(null);
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const dropped = files.map((file) => {
      const segments = (file as File & { webkitRelativePath?: string }).webkitRelativePath?.split('/') ?? [];
      const relativePath = segments.length > 1 ? segments.slice(1).join('/') : file.name;
      return { file, relativePath };
    });
    const cat = categorize(dropped);
    const summary =
      `picked ${dropped.length} file(s) — ` +
      `${cat.filter((c) => c.kind === 'asset').length} asset, ` +
      `${cat.filter((c) => c.kind === 'metadata_csv').length} metadata.csv, ` +
      `${cat.filter((c) => c.kind === 'raw_file').length} raw`;
    setLastDropDebug(summary);
    // eslint-disable-next-line no-console
    console.log('[upload]', summary, cat);
    try {
      await handleFiles(cat);
    } catch (err) {
      setDropError(`Pick handler failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  // Auto-open the folder chooser on mount when the parent page asks
  // for it. Done after one paint so the click is firmly inside the
  // post-navigation user-gesture window.
  useEffect(() => {
    if (!autoOpenChooser) return;
    const t = window.setTimeout(() => inputRef.current?.click(), 50);
    return () => window.clearTimeout(t);
  }, [autoOpenChooser]);

  // Warn before navigating away mid-upload.
  useEffect(() => {
    const inFlight = items.some((i) => i.state === 'uploading' || i.state === 'queued');
    if (!inFlight) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [items]);

  return (
    <section className="mt-6 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-5">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-base font-semibold">Step 4 · Upload your batch</h2>
        {batchId ? (
          <code className="text-xs text-[color:var(--color-text-secondary)]">{batchId}</code>
        ) : null}
      </div>
      <p className="mt-3 text-sm text-[color:var(--color-text-secondary)]">
        Drag your delivery folder here. We'll generate a unique batch ID, walk the contents,
        and upload each file directly to storage. Multiple files upload in parallel; resumes
        automatically if your connection drops mid-upload.
      </p>

      {batchError ? (
        <p className="mt-3 text-sm text-[color:var(--color-warning)]">{batchError}</p>
      ) : null}
      {dropError ? (
        <p className="mt-3 text-sm text-[color:var(--color-warning)]">{dropError}</p>
      ) : null}
      {lastDropDebug && !dropError ? (
        <p className="mt-3 text-xs text-[color:var(--color-text-secondary)]">{lastDropDebug}</p>
      ) : null}

      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onPickClick}
        role="button"
        tabIndex={0}
        className={`mt-4 cursor-pointer rounded border-2 border-dashed px-6 py-12 text-center transition ${
          dragActive
            ? 'border-[color:var(--color-crimson)] bg-[color:var(--color-bg-primary)]'
            : 'border-[color:var(--color-border)]'
        }`}
      >
        <p className="text-sm">
          <span className="font-medium">Drop your batch folder here</span>
          {' '}or click to choose.
        </p>
        <p className="mt-2 text-xs text-[color:var(--color-text-secondary)]">
          Any file or folder structure accepted.
        </p>
        {requestingBatch ? (
          <p className="mt-3 text-xs text-[color:var(--color-text-secondary)]">Generating batch ID…</p>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        // @ts-expect-error — webkitdirectory is a non-standard but widely supported attribute
        webkitdirectory=""
        directory=""
        className="hidden"
        onChange={onPicked}
      />

      {items.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between text-xs text-[color:var(--color-text-secondary)]">
            <span>{items.filter((i) => i.state === 'done').length} of {items.length} files</span>
            <span>{overallProgress}%</span>
          </div>
          <div className="h-1 overflow-hidden rounded bg-[color:var(--color-border)]">
            <div
              className="h-full bg-[color:var(--color-crimson)] transition-[width]"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <ul className="mt-4 max-h-72 overflow-auto rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] text-xs">
            {items.map((it) => (
              <li
                key={it.id}
                className="flex items-center justify-between gap-3 border-b border-[color:var(--color-border)] px-3 py-2 last:border-b-0"
              >
                <span className="truncate font-mono">{it.categorized.relativePath}</span>
                <span className={stateColor(it.state)}>{stateLabel(it)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {allDone && (
        <div className="mt-6 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] p-4 text-sm">
          <p className="font-medium">All files uploaded</p>
          <p className="mt-1 text-[color:var(--color-text-secondary)]">
            Submit your upload to let DFX know it's ready for review. A super-admin will
            validate and contact you if anything needs to be redelivered.
          </p>
          {notifyState === 'sent' ? (
            <p className="mt-3 text-sm text-[color:var(--color-success,#4ade80)]">
              ✓ Upload submitted.
            </p>
          ) : (
            <button
              type="button"
              onClick={notifyDfx}
              disabled={notifyState === 'sending'}
              className="mt-3 rounded bg-[color:var(--color-crimson)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {notifyState === 'sending' ? 'Submitting…' : 'Submit Upload'}
            </button>
          )}
          {notifyError ? (
            <p className="mt-2 text-xs text-[color:var(--color-warning)]">{notifyError}</p>
          ) : null}
        </div>
      )}

      {anyFailed && (
        <p className="mt-4 text-sm text-[color:var(--color-warning)]">
          Some files failed to upload. Refresh and re-drop them — tus resumes from where it
          left off if the file paths and batch ID are the same.
        </p>
      )}
    </section>
  );
}

function stateLabel(it: UploadItem): string {
  if (it.state === 'queued') return 'queued';
  if (it.state === 'uploading') {
    const pct = it.bytesTotal === 0 ? 0 : Math.floor((it.bytesUploaded / it.bytesTotal) * 100);
    return `${pct}%`;
  }
  if (it.state === 'done') return '✓';
  if (it.state === 'failed') return 'failed';
  return '';
}

function stateColor(state: UploadItem['state']): string {
  if (state === 'done') return 'text-[color:var(--color-success,#4ade80)]';
  if (state === 'failed') return 'text-[color:var(--color-warning)]';
  return 'text-[color:var(--color-text-secondary)]';
}
